import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaClock,
  FaEye,
  FaHeart,
  FaMapMarkerAlt
} from 'react-icons/fa';

import UserModal from '../components/UserModal';
import '../css/ProfileViews.css';

function ProfileViews() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [views, setViews] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [loading, setLoading] = useState(true);
  const [openingUserId, setOpeningUserId] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const token = localStorage.getItem('authToken');

  const authorizationHeader = useMemo(() => {
    if (!token) return null;

    return token.startsWith('Bearer ')
      ? token
      : `Bearer ${token}`;
  }, [token]);

  useEffect(() => {
    document.body.classList.add('profile-views-body');

    return () => {
      document.body.classList.remove('profile-views-body');
    };
  }, []);

  useEffect(() => {
    if (!authorizationHeader) {
      setLoading(false);
      setErrorMessage(
        t(
          'views.authenticationRequired',
          'Vous devez être connecté pour voir les visites de votre profil.'
        )
      );
      return;
    }

    const controller = new AbortController();

    const loadViews = async () => {
      setLoading(true);
      setErrorMessage('');

      try {
        /*
         * On récupère les vues AVANT de les marquer comme vues.
         * Si ton endpoint retourne seulement les nouvelles vues,
         * les marquer avant la récupération rendrait la liste vide.
         */
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/users/profile-views`,
          {
            headers: {
              Authorization: authorizationHeader
            },
            signal: controller.signal
          }
        );

        if (!response.ok) {
          throw new Error(
            `Profile views request failed: ${response.status}`
          );
        }

        const data = await response.json();

        const sortedViews = Array.isArray(data)
          ? [...data].sort(
              (first, second) =>
                new Date(second.viewedAt) -
                new Date(first.viewedAt)
            )
          : [];

        setViews(sortedViews);

        await fetch(
          `${process.env.REACT_APP_API_URL}/api/users/profile-views/mark-seen`,
          {
            method: 'POST',
            headers: {
              Authorization: authorizationHeader
            },
            signal: controller.signal
          }
        ).catch((error) => {
          console.warn(
            '⚠️ Unable to mark profile views as seen:',
            error
          );
        });

        window.dispatchEvent(
          new Event('refresh-notifications')
        );
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error(
            '❌ Failed to fetch profile views:',
            error
          );

          setErrorMessage(
            t(
              'views.loadError',
              'Impossible de charger les personnes qui ont consulté votre profil.'
            )
          );
        }
      } finally {
        setLoading(false);
      }
    };

    loadViews();

    return () => controller.abort();
  }, [authorizationHeader, t]);

  const openUserModal = async (userId) => {
    if (!authorizationHeader || openingUserId) return;

    setOpeningUserId(userId);
    setErrorMessage('');

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/users/${userId}/details`,
        {
          headers: {
            Authorization: authorizationHeader
          }
        }
      );

      if (!response.ok) {
        throw new Error(
          `User details request failed: ${response.status}`
        );
      }

      const data = await response.json();

      setSelectedUser(data);
      setShowModal(true);
    } catch (error) {
      console.error(
        '❌ Failed to open viewed profile:',
        error
      );

      setErrorMessage(
        t(
          'views.openProfileError',
          'Impossible d’ouvrir ce profil. Le compte a peut-être été supprimé.'
        )
      );
    } finally {
      setOpeningUserId(null);
    }
  };

  const handleLovely = async () => {
    if (!selectedUser || !authorizationHeader) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/lovely`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: authorizationHeader
          },
          body: JSON.stringify({
            receiverId: selectedUser.id
          })
        }
      );

      if (!response.ok) {
        throw new Error(
          `Lovely request failed: ${response.status}`
        );
      }
    } catch (error) {
      console.error(
        '❌ Failed to like profile:',
        error
      );

      setErrorMessage(
        t(
          'views.likeError',
          'Impossible d’aimer ce profil pour le moment.'
        )
      );
    }
  };

  const handleChat = () => {
    if (!selectedUser) return;

    localStorage.setItem(
      'selectedChatUser',
      JSON.stringify(selectedUser)
    );

    setShowModal(false);
    navigate('/chat');
  };

  const formatViewedAt = (value) => {
    if (!value) {
      return t(
        'views.unknownDate',
        'Date inconnue'
      );
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return t(
        'views.unknownDate',
        'Date inconnue'
      );
    }

    return new Intl.DateTimeFormat(
      i18n.language?.startsWith('fr')
        ? 'fr-FR'
        : 'en-US',
      {
        dateStyle: 'medium',
        timeStyle: 'short'
      }
    ).format(date);
  };

  return (
    <main className="profile-views-page">
      <section className="profile-views-container">
        <header className="profile-views-header">
          <span className="profile-views-header-icon">
            <FaEye />
          </span>

          <div>
            <h1>
              {t(
                'views.title',
                'Visites du profil'
              )}
            </h1>

            <p>
              {t(
                'views.subtitle',
                'Découvrez les personnes qui ont consulté votre profil.'
              )}
            </p>
          </div>

          {!loading && (
            <span className="profile-views-count">
              {views.length}
            </span>
          )}
        </header>

        {errorMessage && (
          <div
            className="profile-views-error"
            role="alert"
          >
            {errorMessage}
          </div>
        )}

        {loading ? (
          <div className="profile-views-state">
            <div className="profile-views-spinner" />

            <p>
              {t(
                'common.loading',
                'Chargement...'
              )}
            </p>
          </div>
        ) : views.length === 0 ? (
          <div className="profile-views-empty">
            <span>
              <FaEye />
            </span>

            <h2>
              {t(
                'views.emptyTitle',
                'Aucune visite pour le moment'
              )}
            </h2>

            <p>
              {t(
                'views.emptyDescription',
                'Lorsqu’une personne consultera votre profil, elle apparaîtra ici.'
              )}
            </p>
          </div>
        ) : (
          <div className="profile-views-list">
            {views.map((view) => {
              const isOpening =
                openingUserId === view.id;

              return (
                <button
                  key={`${view.id}-${view.viewedAt}`}
                  type="button"
                  className="profile-view-card"
                  onClick={() =>
                    openUserModal(view.id)
                  }
                  disabled={isOpening}
                >
                  <div className="profile-view-avatar-wrapper">
                    <img
                      src={
                        view.mediaUrls?.[0] ||
                        '/default-profile.jpg'
                      }
                      alt={
                        view.name ||
                        t(
                          'views.profile',
                          'Profil'
                        )
                      }
                      className="profile-view-avatar"
                      onError={(event) => {
                        event.currentTarget.src =
                          '/default-profile.jpg';
                      }}
                    />

                    <span
                      className={`profile-view-online-dot ${
                        view.online
                          ? 'is-online'
                          : 'is-offline'
                      }`}
                    />
                  </div>

                  <div className="profile-view-information">
                    <div className="profile-view-name-row">
                      <h2>
                        {view.name ||
                          t(
                            'views.unknownUser',
                            'Utilisateur'
                          )}

                        {view.age && (
                          <span>
                            , {view.age}
                          </span>
                        )}
                      </h2>

                      <FaHeart />
                    </div>

                    {view.location && (
                      <p className="profile-view-location">
                        <FaMapMarkerAlt />
                        {view.location}
                      </p>
                    )}

                    <p className="profile-view-date">
                      <FaClock />
                      {formatViewedAt(
                        view.viewedAt
                      )}
                    </p>
                  </div>

                  <span className="profile-view-open-label">
                    {isOpening
                      ? t(
                          'views.opening',
                          'Ouverture...'
                        )
                      : t(
                          'views.viewProfile',
                          'Voir le profil'
                        )}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </section>

      <UserModal
        show={showModal}
        handleClose={() => {
          setShowModal(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onLovely={handleLovely}
        onChat={handleChat}
      />
    </main>
  );
}

export default ProfileViews;